import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { JsonEditor } from "json-edit-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { deleteForm, getForms, getFormTypes, addForm } from "./formsActions";
import { DeleteOutlineOutlined, EditOutlined } from "@mui/icons-material";
import { ILookupValue } from "../../models/common/common";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import { IAddFormRequestBody, IForm } from "../../models/admin/Forms/Forms";
import { Add as AddIcon } from "@mui/icons-material";
import FormDialog from "./FormDialog";

interface IOption {
  id: string;
  name: string;
  code: string;
}

interface IConditionOption {
  source: string;
  value: string;
  attribute: "disabled" | "hidden";
}

interface ICondition {
  type: "and" | "or";
  conditionOptions: IConditionOption[];
}

interface Field {
  name: string;
  value: string;
  label: string;
  type: string;
  required: boolean;
  disabled: boolean;
  placeholder?: string;
  hidden?: boolean;
  condition?: ICondition;
  asyncType?: string;
  asyncOptionsFn?: string;
  options?: IOption[];
  asyncValueFn?: string;
}

interface Section {
  layoutColumns: number;
  fields: Field[];
  sectionHeading?: string;
}

interface IFormState {
  formHeading?: string;
  sections?: Section[];
}

const Forms = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");

  const { formTypeOptions, forms } = useAppSelector((state) => state.forms);
  const { isSuccess, isError, calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );

  const [addFormRequestBody, setAddFormRequestBody] =
    useState<IAddFormRequestBody>({
      createdBy: "Admin",
      formCode: "",
      formContent: "",
      formName: "",
      updatedBy: "Admin",
    });
  const [json, setJson] = useState<IFormState>({});
  const [isAddForm, setIsAddForm] = useState<boolean>(true);
  const [deleteFormCode, setDeleteFormCode] = useState<string>("");
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [editFormDialogOpen, setEditFormDialogOpen] = useState<boolean>(false);
  const [deleteFormAlertDialogOpen, setDeleteFormAlertDialogOpen] =
    useState<boolean>(false);
  const [addFormModalOpen, setAddFormModalOpen] = useState<boolean>(false);

  const handleFormTypeChange = useCallback(
    (event: SelectChangeEvent) => {
      const selectedFormType = formTypeOptions.find(
        (option: ILookupValue) => option.code === event.target.value
      );
      if (selectedFormType) {
        setAddFormRequestBody((prevState) => ({
          ...prevState,
          formCode: selectedFormType.code,
          formName: selectedFormType.label,
        }));
        setJson({});
      }
    },

    [formTypeOptions]
  );

  const handleJsonChange = useCallback((data: any) => {
    setJson(data);
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      dispatch(
        addForm({
          ...addFormRequestBody,
          formContent: JSON.stringify(json),
        })
      );
    },
    [dispatch, addFormRequestBody, json]
  );

  const handleCancelClick = useCallback(() => {
    setAddFormRequestBody((prevState) => ({
      ...prevState,
      formCode: "",
      formName: "",
    }));
  }, []);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    if (
      isSuccess &&
      (calledReducerType === "forms/addForm" ||
        calledReducerType === "forms/deleteForm")
    ) {
      dispatch(getForms());
    }
  }, [isSuccess, calledReducerType, dispatch]);

  const handleEditIconClick = useCallback((form: IForm) => {
    setIsAddForm(false);
    setAddFormRequestBody(form);
    setJson(JSON.parse(form.formContent));
    setEditFormDialogOpen(true);
  }, []);

  const handleEditFormCancelClick = useCallback(() => {
    setEditFormDialogOpen(false);
    setIsAddForm(true);
    setAddFormRequestBody((prevState) => ({
      ...prevState,
      formCode: "",
      formName: "",
    }));
  }, []);

  const handleDeleteIconClick = useCallback((formCode: string) => {
    setDeleteFormCode(formCode);
    setDeleteFormAlertDialogOpen(true);
  }, []);

  const handleAlertDialogCancelClick = () => {
    setDeleteFormCode("");
    setDeleteFormAlertDialogOpen(false);
  };

  const handleDeleteForm = useCallback(() => {
    dispatch(deleteForm(deleteFormCode));
  }, [dispatch, deleteFormCode]);

  const columns: IColumn[] = [
    { label: "Form", accessorKey: "formName" },
    { label: "Product", accessorKey: "product" },

    {
      label: "Actions",
      styles: { textAlign: "center" },
      Cell: (row: any) => (
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            justifyContent: "center",
          }}
        >
          <IconButton onClick={() => handleEditIconClick(row)}>
            <Tooltip title="Edit">
              <EditOutlined />
            </Tooltip>
          </IconButton>
          <IconButton onClick={() => handleDeleteIconClick(row.formCode)}>
            <Tooltip title="Delete">
              <DeleteOutlineOutlined />
            </Tooltip>
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    dispatch(getFormTypes());
    dispatch(getForms());
  }, [dispatch]);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "forms/addForm" ||
        calledReducerType === "forms/deleteForm")
    ) {
      setMessageDialogOpen(true);
      setAddFormRequestBody((prevState) => ({
        ...prevState,
        formCode: "",
        formName: "",
        formContent: "",
        effectiveDate: "",
        expirationDate: "",
      }));
      setIsAddForm(true);
      setEditFormDialogOpen(false);
    }
  }, [isSuccess, isError, calledReducerType]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          paddingY: isMobile ? 1 : 3,
        }}
        id="admin_forms"
        data-test-id="admin_forms"
        aria-label="Admin Forms Section"
      >
        <Box 
          sx={{ display: "flex", justifyContent: "flex-end" }}
          id="admin_forms_header"
          data-test-id="admin_forms_header"
          aria-label="Forms Header"
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddFormModalOpen(true)}
            sx={{ textTransform: "none", borderRadius: "4px" }}
            id="admin_forms_add_button"
            data-test-id="admin_forms_add_button"
            aria-label="Add new form"
          >
            Add Form
          </Button>
        </Box>
        <CustomTable
          columns={columns}
          data={forms}
          displaySkeleton={isLoading && calledReducerType === "forms/getForms"}
        />
      </Box>
      <FormDialog
        open={addFormModalOpen}
        onClose={() => {
          setAddFormModalOpen(false);
          handleCancelClick();
        }}
      >
        <form
          onSubmit={(e) => {
            handleSubmit(e);
            setAddFormModalOpen(false);
          }}
          style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          id="admin_forms_add_form"
          data-test-id="admin_forms_add_form"
          aria-label="Add form form"
        >
          <FormControl fullWidth>
            <InputLabel id="admin_forms_select_label">Forms</InputLabel>
            <Select
              value={isAddForm ? addFormRequestBody.formCode : ""}
              label="Forms"
              onChange={handleFormTypeChange}
              id="admin_forms_select"
              data-test-id="admin_forms_select"
              aria-label="Select form type"
              labelId="admin_forms_select_label"
            >
              {formTypeOptions
                .filter(
                  (option: ILookupValue) =>
                    !forms.some(
                      (form: IAddFormRequestBody) =>
                        form.formCode === option.code
                    )
                )
                .map((option: ILookupValue) => (
                  <MenuItem 
                    key={option.code} 
                    value={option.code}
                    id={`admin_forms_option_${option.code}`}
                    data-test-id={`admin_forms_option_${option.code}`}
                  >
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {isAddForm && addFormRequestBody.formCode ? (
            <>
              <JsonEditor 
                data={json} 
                setData={handleJsonChange}
                id="admin_forms_json_editor"
                data-test-id="admin_forms_json_editor"
                aria-label="Form JSON editor"
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "20px",
                  marginTop: 2,
                }}
                id="admin_forms_actions"
                data-test-id="admin_forms_actions"
                aria-label="Form actions"
              >
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ width: "max-content", borderRadius: "4px" }}
                  id="admin_forms_save_button"
                  data-test-id="admin_forms_save_button"
                  aria-label="Save form"
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleCancelClick();
                    setAddFormModalOpen(false);
                  }}
                  sx={{ width: "max-content", borderRadius: "4px" }}
                  id="admin_forms_cancel_button"
                  data-test-id="admin_forms_cancel_button"
                  aria-label="Cancel form creation"
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : null}
        </form>
      </FormDialog>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
      <AlertDialog
        open={deleteFormAlertDialogOpen}
        handleCancelClick={handleAlertDialogCancelClick}
        handleOkClick={handleDeleteForm}
        title="Deleting this form will effect the end user, is it ok?"
      />
      <Dialog 
        open={editFormDialogOpen} 
        fullScreen 
        sx={{ height: "100vh" }}
        id="admin_forms_edit_dialog"
        data-test-id="admin_forms_edit_dialog"
        aria-label="Edit form dialog"
      >
        <form onSubmit={handleSubmit}>
          <AppBar sx={{ position: "fixed" }}>
            <Toolbar>
              <Typography 
                sx={{ ml: 2, flex: 1 }} 
                variant="h6" 
                component="div"
                id="admin_forms_edit_title"
                data-test-id="admin_forms_edit_title"
              >
                {addFormRequestBody.formName}
              </Typography>
              <Button 
                autoFocus 
                color="inherit" 
                type="submit"
                id="admin_forms_edit_save"
                data-test-id="admin_forms_edit_save"
                aria-label="Save form changes"
              >
                Save
              </Button>
              <Button
                autoFocus
                color="inherit"
                onClick={handleEditFormCancelClick}
                id="admin_forms_edit_cancel"
                data-test-id="admin_forms_edit_cancel"
                aria-label="Cancel form editing"
              >
                Cancel
              </Button>
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ marginTop: "65px" }}>
            <JsonEditor
              data={json}
              setData={handleJsonChange}
              minWidth={"100%"}
              className="jsonEdior"
              id="admin_forms_edit_json_editor"
              data-test-id="admin_forms_edit_json_editor"
              aria-label="Edit form JSON"
            />
          </DialogContent>
        </form>
      </Dialog>
    </>
  );
};

export default Forms;
